// Lib
import React, { ReactElement, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Immutable from 'immutable';
import classNames from 'classnames';

// Utils
import { isEmpty } from '../../../../common/utils/immutableHelper';
import { getUserId } from '../../../../common/users/utils/userPropertyUtils';
import {
    collapseItemsToFit,
    getAdjustedToolbarGridSize,
} from '../../../workspace/toolbar/utils/toolbarToolListCollapseUtils';
import { isCommentThread } from '../../../../common/elements/utils/elementTypeUtils';
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { useVirtualKeyboardHeightContext } from '../../../utils/keyboard/VirtualKeyboardHeightContext';

// Selectors
import { getCurrentUser } from '../../../user/currentUserSelector';
import { getGridSizeName } from '../../../utils/grid/gridSizeSelector';
import { getSelectedElements } from '../../selection/selectedElementsSelector';

// Components
import ToolbarMeasure from '../../../workspace/toolbar/components/ToolbarMeasure';
import ToolbarItemsList from '../../../workspace/toolbar/components/ToolbarItemsList';

// Constants
import { TOOL_SETS } from '../../../workspace/toolbar/config/toolbarToolConfig';
import { DOCUMENT_MODAL_EDITOR_KEY } from './documentModalConstants';
import { ImMNElement } from '../../../../common/elements/elementModelTypes';
import SELECTED_TOOL_CONFIG from '../../../workspace/toolbar/config/toolDefinitions/toolbarSelectedToolConfig';

// Styles
import './DocumentModalSidebar.scss';

const INLINE_COMMENT_TOOLBAR_ITEMS = [
    { ...SELECTED_TOOL_CONFIG.COLOR, buttonSelector: '.DocumentModal .ColorTool .ColorToolIcon' },
    SELECTED_TOOL_CONFIG.MOVE_TO_TRASH,
];
const DOCUMENT_MODAL_TOOLBAR_ITEMS = [...TOOL_SETS.TEXT_EDITING_INLINE_AND_BLOCK];

interface DocumentModalSidebarProps {
    elementId: string;
    element: ImMNElement;

    isEditable: boolean;
    isEditingDocument: boolean;

    permissions: number;

    dispatchStartEditingDocument: () => void;
}

const DocumentModalSidebar = (props: DocumentModalSidebarProps): ReactElement => {
    const { elementId, isEditable, isEditingDocument, permissions, dispatchStartEditingDocument } = props;

    const [availableHeight, setAvailableHeight] = useState(0);

    const virtualKeyboardHeight = useVirtualKeyboardHeightContext();

    const gridSizeName = useSelector(getGridSizeName);

    const currentUser = useSelector(getCurrentUser);
    const currentUserId = getUserId(currentUser);

    const selectedElements = useSelector(getSelectedElements);

    // adjust the toolbar grid size based on the available height
    const adjustedGridSize = getAdjustedToolbarGridSize(gridSizeName, availableHeight);

    const isSelectingInlineComment = !isEmpty(selectedElements) && selectedElements.every(isCommentThread);

    const toolbarItemsConfig = isSelectingInlineComment ? INLINE_COMMENT_TOOLBAR_ITEMS : DOCUMENT_MODAL_TOOLBAR_ITEMS;

    const renderedToolbarItems = collapseItemsToFit(
        toolbarItemsConfig,
        availableHeight - virtualKeyboardHeight,
        adjustedGridSize.name,
    );

    const toolListId = isSelectingInlineComment
        ? `${elementId}-inline-comment-document-modal`
        : `${elementId}-document-modal`;

    const hideSidebarContent = !isEditable;

    const classes = classNames('DocumentModalSidebar', {
        'hide-sidebar-content': hideSidebarContent,
        disabled: !isEditingDocument && !isSelectingInlineComment,
    });

    const selectedElementIds = isSelectingInlineComment
        ? selectedElements.map(getElementId)
        : Immutable.List<string>([elementId]);

    return (
        <div
            className={classes}
            onClick={!isEditingDocument && !isSelectingInlineComment ? dispatchStartEditingDocument : undefined}
        >
            <ToolbarMeasure setAvailableHeight={setAvailableHeight} />

            {isEditable && (
                <div className="DocumentModalTools">
                    <ToolbarItemsList
                        getComponent={(item) => item.component as React.FC}
                        toolListId={toolListId}
                        items={renderedToolbarItems}
                        currentUser={currentUser}
                        currentUserId={currentUserId}
                        currentBoardId={elementId}
                        gridSize={adjustedGridSize.size}
                        gridSizeName={adjustedGridSize.name}
                        permissions={permissions}
                        selectedElementIds={selectedElementIds}
                        editorKey={DOCUMENT_MODAL_EDITOR_KEY}
                        hasEditorSelection={false}
                    />
                </div>
            )}
        </div>
    );
};

export default DocumentModalSidebar;
